import { useEffect, useRef, useState } from 'react'
import './pipelineVisual.css'

const stages = [
  { key: 'initial', title: '초기개발', runtime: 'Ansible + PM2', proof: 'initialVerified', button: '빌드 · PM2 검증' },
  { key: 'stabilization', title: '안정화', runtime: 'K3s', proof: 'stabilizationVerified', button: 'K3s로 전환' },
  { key: 'production', title: '운영', runtime: 'Kubernetes', proof: 'productionVerified', button: '운영에 반영' },
]
const states = { queued: '대기', running: '실행 중', verified: '검증 완료', failed: '확인 필요' }

export default function ManagedLifecycle({ service, api }) {
  const [data, setData] = useState(null)
  const [commit, setCommit] = useState('')
  const [approved, setApproved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [autoTarget,setAutoTarget]=useState('stabilization')
  const policyLoaded=useRef(false)
  const retry = useRef(null)
  const id = service?.service_id
  const supported = ['estimate-app', 'skynetmanager'].includes(id)
  useEffect(() => {
    setData(null); setCommit(''); setApproved(false); setError(''); retry.current = null;policyLoaded.current=false
    if (!supported) return undefined
    let live = true
    async function poll() {
      try {
        const value = await api(`/services/${id}/lifecycle`)
        if (!live) return
        setData(value)
        if(!policyLoaded.current){setAutoTarget(value.policy?.targetStage||'stabilization');policyLoaded.current=true}
        setCommit(previous => previous || value.releases?.[0]?.sourceCommit || '')
        setError('')
      } catch { if (live) { setData(null); setError('단계별 실행 상태를 불러오지 못했습니다.') } }
    }
    poll(); const timer = setInterval(poll, 5000)
    return () => { live = false; clearInterval(timer) }
  }, [api, id, supported])
  if (!supported) return <section className="deploy-panel managed-lifecycle"><h2>단계별 배포</h2><p>이 서비스는 자동 실행 연결 전입니다. 저장소·개발 프로세스·시험 및 운영 대상을 검증한 후 실행할 수 있습니다.</p></section>
  const release = data?.releases?.find(value => value.sourceCommit === commit)
  const active = data?.runs?.some(run => ['queued', 'running'].includes(run.state))
  const valid = /^[a-f0-9]{40}$/.test(commit)
  async function submit(stage,targetStage,operation) {
    setBusy(true); setError('')
    try {
      const selectedCommit=stage==='process'?data.process.sourceCommit:commit
      const identity = `${id}:${stage}:${targetStage||''}:${operation||''}:${selectedCommit}`
      if (retry.current?.identity !== identity) retry.current = { identity, key: window.crypto.randomUUID() }
      await api(`/services/${id}/lifecycle/runs`, { method: 'POST', body: JSON.stringify({
        stage, sourceCommit: selectedCommit, requestKey: retry.current.key, approveProduction: (stage === 'production'||targetStage==='production') && approved,
        ...(targetStage?{targetStage}:{}),
        ...(operation?{operation}:{}),
      }) })
      setData(await api(`/services/${id}/lifecycle`)); setApproved(false)
      retry.current = null
    } catch (cause) { setError(cause.message || '단계 전환 요청을 확인하지 못했습니다.') }
    finally { setBusy(false) }
  }
  async function savePolicy(enabled){
    setBusy(true);setError('')
    try{
      await api(`/services/${id}/lifecycle/policy`,{method:'PUT',body:JSON.stringify({enabled,targetStage:autoTarget,approveProduction:approved})})
      setData(await api(`/services/${id}/lifecycle`));setApproved(false)
    }catch(cause){setError(cause.message||'자동 실행 설정을 저장하지 못했습니다.')}
    finally{setBusy(false)}
  }
  return <section className="deploy-panel managed-lifecycle" aria-labelledby="managed-lifecycle-heading">
    <div className="deploy-section-heading"><h2 id="managed-lifecycle-heading">단계별 배포</h2>
      <span className="managed-connection">{data?.executionConnected ? '실행기 연결됨' : '실행기 확인 중'}</span></div>
    <p>한 번 요청하면 빌드·PM2 실행, K3s 시험과 검증을 순서대로 진행합니다. 운영 반영 실패 시 이전 버전으로 복구합니다.</p>
    <div className="managed-source"><label htmlFor="managed-commit">소스 커밋</label>
      <input id="managed-commit" value={commit} maxLength={40} spellCheck="false" placeholder="main의 40자리 커밋"
        onChange={event => { setCommit(event.target.value.trim().toLowerCase()); setApproved(false) }} />
      <button type="button" disabled={!data?.executionConnected||!data?.source?.available||busy||active}
        onClick={()=>{setCommit(data.source.commit);setApproved(false)}}>main 최신 커밋</button>
      {data?.releases?.length > 0 && <select aria-label="검증된 이미지 선택" value={release ? commit : ''}
        onChange={event => { setCommit(event.target.value); setApproved(false) }}>
        <option value="">검증된 이미지 선택</option>
        {data.releases.map(value => <option key={value.sourceCommit} value={value.sourceCommit}>{value.sourceCommit.slice(0, 12)}</option>)}
      </select>}
    </div>
    {data?.process?.registered&&<div className="managed-process" aria-label="초기개발 PM2 프로세스">
      <div><strong>개발 PM2</strong><span>{data.process.state==='online'?'실행 중':data.process.state==='stopped'?'정지':data.process.state} · {data.process.sourceCommit.slice(0,12)}</span>
        <small>재시작 {data.process.restarts}회 · 메모리 {Math.round((data.process.memoryBytes||0)/1024**2)} MiB</small></div>
      {['start','restart','stop'].map(operation=><button type="button" key={operation}
        disabled={!data.executionConnected||!data.automationConnected||busy||active||(operation==='start'?data.process.state==='online':data.process.state!=='online')}
        onClick={()=>submit('process',undefined,operation)}>{{start:'시작',restart:'재시작',stop:'정지'}[operation]}</button>)}
      <small>Ansible로 등록된 개발 프로세스만 제어합니다. K3s·운영 Pod는 계속 실행됩니다.</small>
    </div>}
    <div className="managed-auto-actions" aria-label="연속 배포 실행">
      <button type="button" disabled={!data?.executionConnected||!data?.automationConnected||!valid||busy||active} onClick={()=>submit('pipeline','stabilization')}>빌드부터 K3s 검증까지</button>
      <button type="button" disabled={!data?.executionConnected||!data?.automationConnected||!valid||busy||active||!approved} onClick={()=>submit('pipeline','production')}>검증 후 운영까지</button>
    </div>
    <label className="managed-approval"><input type="checkbox" checked={approved} onChange={event => setApproved(event.target.checked)} />
      선택한 이미지 또는 아래 자동 실행 정책의 운영 반영을 승인합니다.</label>
    <details className="managed-individual"><summary>단계별로 직접 실행</summary>
    <ol className="managed-stages">{stages.map((stage, index) => {
      const ready = index === 0 ? valid : index === 1 ? release?.initialVerified : release?.stabilizationVerified
      const verified = release?.[stage.proof]
      const run = data?.runs?.find(value => value.sourceCommit === commit && value.stage === stage.key)
      const state = run && ['running','queued','failed'].includes(run.state) ? run.state : verified ? 'verified' : 'waiting'
      return <li key={stage.key} className={'managed-state-' + state}>
        <span className="managed-stage-number">{state === 'verified' ? '✓' : state === 'failed' ? '×' : index + 1}</span><div><h3>{stage.title}</h3><p>{stage.runtime}</p>
          <small>{verified ? '검증 기록 확인됨' : '검증 대기'}</small></div>
        <button type="button" disabled={!data?.executionConnected || !ready || busy || active || (index === 2 && !approved)} onClick={() => submit(stage.key)}>{stage.button}</button>
      </li>
    })}</ol>
    </details>
    <div className="managed-auto-policy">
      <div><h3>main 변경 자동 실행</h3><p>{data?.policy?.enabled?`사용 중 · ${data.policy.targetStage==='production'?'운영 반영까지':'K3s 검증까지'}`:'꺼짐 · 직접 실행만 사용'}</p></div>
      <label>자동 실행 범위<select aria-label="자동 실행 범위" value={autoTarget} onChange={e=>{setAutoTarget(e.target.value);setApproved(false)}}>
        <option value="stabilization">K3s 검증까지</option><option value="production">검증 후 운영까지</option></select></label>
      <button type="button" disabled={!data?.executionConnected||!data?.automationConnected||busy||(autoTarget==='production'&&!approved)} onClick={()=>savePolicy(true)}>자동 실행 저장</button>
      {data?.policy?.enabled&&<button type="button" disabled={busy} onClick={()=>savePolicy(false)}>자동 실행 끄기</button>}
      <small>main 변경을 감지해 같은 커밋을 한 번만 실행합니다. 실패한 단계 이후에는 진행하지 않습니다. 끄면 다음 자동 단계부터 중단하며 실행 중인 명령은 마무리합니다.</small>
    </div>
    {release?.scaleVerified && release?.recoveryVerified && <p className="managed-proof">K3s에서 Pod 교체와 2 → 3 → 2 자동확장·축소를 검증했습니다.</p>}
    {error && <p className="deploy-error" role="alert">{error}</p>}
    {!!data?.runs?.length && <div className="managed-runs" aria-label="최근 단계 실행">{data.runs.slice(0, 3).map(run => <p key={run.runId}>
      <strong>{run.stage==='pipeline'?'연속 배포':run.stage==='process'?`개발 PM2 ${{start:'시작',stop:'정지',restart:'재시작'}[run.operation]||''}`:stages.find(stage => stage.key === run.stage)?.title} · {states[run.state] || '상태 확인'}{run.trigger==='git-main'?' · main 자동 감지':''}</strong>
      <span>{run.sourceCommit.slice(0, 12)}{run.code ? ` · ${run.code}` : ''}</span>
      {run.steps?.length>0&&<span>{run.steps.map(step=>`${stages.find(s=>s.key===step.stage)?.title} ${states[step.state]||'대기'}`).join(' → ')}</span>}
    </p>)}</div>}
  </section>
}
